import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  IconButton,
  Grid,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, startOfMonth, endOfMonth } from 'date-fns';

import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { meetingService, Meeting } from '../services/meetingService';
import { formatTime, formatDateTime } from '../utils/dateUtils';

type ViewMode = 'day' | 'week' | 'month';

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTenant, refreshKey } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Load meetings data
  useEffect(() => {
    const loadCalendarData = async () => {
      if (!user || !currentTenant) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Calculate date range based on view mode
        let start: Date;
        let end: Date;

        switch (viewMode) {
          case 'day':
            start = new Date(currentDate);
            start.setHours(0, 0, 0, 0);
            end = new Date(currentDate);
            end.setHours(23, 59, 59, 999);
            break;
          case 'week':
            start = startOfWeek(currentDate);
            end = endOfWeek(currentDate);
            break;
          case 'month':
            start = startOfWeek(startOfMonth(currentDate));
            end = endOfWeek(endOfMonth(currentDate));
            break;
        }

        // Convert to Unix timestamps in milliseconds
        const fromDate = start.getTime();
        const toDate = end.getTime();

        // Load meetings for the specific date range
        const meetingsData = await meetingService.getMeetingsByDateRange(
          currentTenant.id,
          fromDate,
          toDate
        );
        setMeetings(meetingsData);
      } catch (err) {
        console.error('Error loading calendar data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load calendar data');
      } finally {
        setLoading(false);
      }
    };

    loadCalendarData();
  }, [user, currentTenant, refreshKey, currentDate, viewMode]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'done':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'noshow':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getMeetingsForDate = (date: Date) => {
    return meetings.filter(meeting =>
      isSameDay(new Date(meeting.scheduled_at), date)
    );
  };

  const handlePrevious = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(subDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
    }
  };

  const handleNext = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleMeetingClick = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setDetailDialogOpen(true);
  };

  const renderDayView = () => {
    const dayMeetings = getMeetingsForDate(currentDate);

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          {format(currentDate, 'EEEE, MMMM d, yyyy')}
        </Typography>
        <Grid container spacing={2}>
          {dayMeetings.length > 0 ? (
            dayMeetings.map((meeting) => (
              <Grid item xs={12} sm={6} md={4} key={meeting.id}>
                <Card
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                  onClick={() => handleMeetingClick(meeting)}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {meeting.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {formatTime(meeting.scheduled_at)} - {meeting.duration_minutes} min
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      Patient: {meeting.patient.full_name}
                    </Typography>
                    <Chip
                      label={meeting.status.toUpperCase()}
                      color={getStatusColor(meeting.status) as any}
                      size="small"
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center">
                No meetings scheduled for this day
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate);
    const weekEnd = endOfWeek(currentDate);
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
        </Typography>
        <Grid container spacing={1}>
          {days.map((day) => {
            const dayMeetings = getMeetingsForDate(day);
            return (
              <Grid item xs={12/7} key={day.toISOString()}>
                <Paper sx={{ p: isMobile ? 0.5 : 1, minHeight: isMobile ? 120 : 200 }}>
                  <Typography variant="subtitle2" gutterBottom align="center">
                    {format(day, 'EEE d')}
                  </Typography>
                  {dayMeetings.map((meeting) => {
                    const meetingTooltip = `${meeting.title} - ${formatTime(meeting.scheduled_at)} (${meeting.duration_minutes}min)`;
                    return (
                      <Tooltip
                        key={meeting.id}
                        title={meetingTooltip}
                        placement="top"
                        arrow
                      >
                        <Box
                          sx={{
                            mb: isMobile ? 0.5 : 1,
                            p: isMobile ? 0.5 : 1,
                            bgcolor: getStatusColor(meeting.status) + '.light',
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.8 },
                            minHeight: isMobile ? '24px' : 'auto',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          onClick={() => handleMeetingClick(meeting)}
                        >
                          {isMobile ? (
                            <Typography variant="caption" noWrap sx={{ fontSize: '0.75rem', fontWeight: 'medium' }}>
                              {formatTime(meeting.scheduled_at)} - {meeting.patient.full_name}
                            </Typography>
                          ) : (
                            <>
                              <Typography variant="caption" display="block">
                                {formatTime(meeting.scheduled_at)}
                              </Typography>
                              <Typography variant="body2" noWrap>
                                {meeting.patient.full_name}
                              </Typography>
                              <Chip
                                label={meeting.status[0].toUpperCase()}
                                color={getStatusColor(meeting.status) as any}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: startDate, end: endDate });

    // Group days into weeks
    const weeks: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          {format(currentDate, 'MMMM yyyy')}
        </Typography>

        {/* Week headers */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Grid item xs={12/7} key={day}>
              <Typography variant="subtitle2" align="center" color="text.secondary">
                {day}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Calendar grid */}
        {weeks.map((week, weekIndex) => (
          <Grid container spacing={1} key={weekIndex} sx={{ mb: 1 }}>
            {week.map((day) => {
              const dayMeetings = getMeetingsForDate(day);
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();

              return (
                <Grid item xs={12/7} key={day.toISOString()}>
                  <Paper
                    sx={{
                      p: isMobile ? 0.5 : 1,
                      minHeight: isMobile ? 80 : 100,
                      bgcolor: isCurrentMonth ? 'background.paper' : 'action.disabledBackground',
                      opacity: isCurrentMonth ? 1 : 0.6
                    }}
                  >
                    <Typography
                      variant="caption"
                      align="center"
                      display="block"
                      sx={{ fontWeight: isSameDay(day, new Date()) ? 'bold' : 'normal' }}
                    >
                      {format(day, 'd')}
                    </Typography>
                    {dayMeetings.slice(0, isMobile ? 3 : 2).map((meeting) => {
                      const meetingTooltip = `${meeting.title}\n${formatTime(meeting.scheduled_at)} (${meeting.duration_minutes}min)\nPatient: ${meeting.patient.full_name}\nTherapist: ${meeting.therapist.full_name}`;

                      return (
                        <Tooltip
                          key={meeting.id}
                          title={meetingTooltip}
                          placement="top"
                          arrow
                        >
                          <Box
                            sx={{
                              mb: isMobile ? 0.25 : 0.5,
                              p: isMobile ? 0.25 : 0.5,
                              bgcolor: getStatusColor(meeting.status) + '.light',
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 },
                              minHeight: isMobile ? '20px' : 'auto',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            onClick={() => handleMeetingClick(meeting)}
                          >
                            {isMobile ? (
                              <Typography variant="caption" noWrap sx={{ fontSize: '0.625rem', fontWeight: 'medium' }}>
                                {formatTime(meeting.scheduled_at)} - {meeting.patient.full_name.split(' ')[0]}
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="caption" noWrap display="block">
                                  {formatTime(meeting.scheduled_at)}
                                </Typography>
                                <Typography variant="caption" noWrap display="block" sx={{ opacity: 0.8 }}>
                                  {meeting.patient.full_name}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })}
                    {dayMeetings.length > (isMobile ? 3 : 2) && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.625rem' : '0.75rem' }}>
                        +{dayMeetings.length - (isMobile ? 3 : 2)} more
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading calendar: {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={3} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Calendar
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/meetings')}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
            padding: { xs: '6px 16px', sm: '8px 22px' }
          }}
        >
          Schedule Meeting
        </Button>
      </Box>

      {/* Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            onClick={handlePrevious}
            sx={{ padding: { xs: '4px', sm: '8px' } }}
          >
            <ChevronLeftIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<TodayIcon />}
            onClick={handleToday}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '4px 8px', sm: '6px 16px' }
            }}
          >
            Today
          </Button>
          <IconButton
            onClick={handleNext}
            sx={{ padding: { xs: '4px', sm: '8px' } }}
          >
            <ChevronRightIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }} />
          </IconButton>
        </Box>

        <ButtonGroup
          variant="outlined"
          sx={{
            '& .MuiButton-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              padding: { xs: '4px 8px', sm: '6px 16px' }
            }
          }}
        >
          <Button
            variant={viewMode === 'day' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('day')}
          >
            Day
          </Button>
          <Button
            variant={viewMode === 'week' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('week')}
          >
            Week
          </Button>
          <Button
            variant={viewMode === 'month' ? 'contained' : 'outlined'}
            onClick={() => setViewMode('month')}
          >
            Month
          </Button>
        </ButtonGroup>
      </Box>

      {!currentTenant && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {user?.role === 'admin'
            ? 'No tenant selected. Please select a tenant from the top bar.'
            : 'No tenant assigned. Please contact your administrator.'
          }
        </Alert>
      )}

      {/* Calendar Views */}
      {viewMode === 'day' && renderDayView()}
      {viewMode === 'week' && renderWeekView()}
      {viewMode === 'month' && renderMonthView()}

      {/* Meeting Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedMeeting && (
          <>
            <DialogTitle>
              {selectedMeeting.title}
            </DialogTitle>
            <DialogContent>
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Patient
                </Typography>
                <Typography variant="body1">
                  {selectedMeeting.patient.full_name}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Date & Time
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedMeeting.scheduled_at)}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Duration
                </Typography>
                <Typography variant="body1">
                  {selectedMeeting.duration_minutes} minutes
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={selectedMeeting.status.toUpperCase()}
                  color={getStatusColor(selectedMeeting.status) as any}
                />
              </Box>

              {selectedMeeting.session_type && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Session Type
                  </Typography>
                  <Typography variant="body1">
                    {selectedMeeting.session_type}
                  </Typography>
                </Box>
              )}

              {selectedMeeting.notes && (
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body1">
                    {selectedMeeting.notes}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>
                Close
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setDetailDialogOpen(false);
                  navigate(`/meetings/${selectedMeeting.id}`);
                }}
              >
                View Details
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default CalendarPage;